import { apiGet, apiThrow } from './api';
import { poDisplayText } from './orderPdfMeta';
import { getOrderNumber } from './orderTitle';
import { CSV_EXT } from './gagCsvExport';

/**
 * Export ONE order and its line items as CSV (opens in Excel; was the `.xls` "Excel" option until 2026-10-28).
 *
 * Product owner, 2026-08-10, asked for *"an export excell"* in the order actions
 * menu, and — offered the filtered-LIST export instead — chose **this order**.
 * So this is deliberately not `handleExport`'s little sibling: that one exports
 * what the page is showing, this one exports the row you opened.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🟢 NO SERVER CHANGE, AND NO GATE WIDENED — MEASURED BEFORE IT WAS WRITTEN.
 * ═══════════════════════════════════════════════════════════════════════════
 * `GET /api/orders/export` (`server/routes/orders.js:421`) already takes
 * `?ids=<csv>` and `format=csv` (the old `excel` token is an alias). Its `ids` branch narrows in SQL through
 * `visibleOrdersClause(req)` and **both** branches are then post-filtered row by
 * row through `await canViewOrder(order, req)` (`:507-521`) — the same predicate
 * the list endpoints use. So one id is just the degenerate case of a filter this
 * route has always supported.
 *
 * 🔑 **THERE IS THEREFORE NO CLIENT GATE ON THIS ACTION, AND THAT IS A
 * MEASUREMENT RATHER THAN A PREFERENCE.** The obvious worry is the 2026-08-05
 * accounting-leak class: `canViewOrderAccounting` is strictly NARROWER than
 * `canViewOrder`, and `sub_department_lead` is seeded
 * `can_view_department_orders = 1` / `can_view_order_accounting = 0`, so an
 * export carrying cost codes would hand that role data the API refuses it
 * elsewhere. **Checked against the route rather than assumed: the export emits
 * none of them.** Its 31 columns are written out explicitly and `cost_code`,
 * `cost_code_name`, `payment_type_name`, `budget_pot_name` and all three FK ids
 * — i.e. every entry of `ACCOUNTING_ITEM_FIELDS` (`routes/orders.js:191`) —
 * appear **nowhere between `:421` and the end of the handler** (grep: zero
 * hits). The orders query is `SELECT o.*`, but the CSV builder never spreads a
 * row; it names each field. So the question `canViewOrderAccounting` answers
 * does not arise here, and anyone who can see the row may export it.
 * ⚠️ **If a column is ever added to that export, re-run that check first.** An
 * accounting column added there would leak through this menu item to every
 * viewer of the row, and no client gate would be standing in the way.
 *
 * 🔑 One more property of the route worth knowing before "improving" the error
 * handling: an id the caller may not see is **DROPPED, not refused**, so a
 * mis-scoped call answers 200 with a headers-only file rather than 403. That is
 * deliberate anti-enumeration (`:511-517`) — do not turn an empty file into a
 * thrown error, because the two are indistinguishable from here by design.
 *
 * 🪤 The filename uses the ORDER's own reference, not `orders-export-<date>` —
 * a per-order download landing in Downloads beside three others all called
 * `orders-export-2026-08-10.csv` is the defect this whole item exists to avoid.
 * `.csv` because that is what the route sends (`Content-Type: text/csv`, a BOM
 * so Excel reads the UTF-8). It was `.xls` / `application/vnd.ms-excel` over
 * the same bytes until 2026-10-28, which made Excel warn on open.
 */
export async function exportSingleOrderCsv(order) {
  if (!order || order.id == null) throw new Error('Export failed: no order');

  const params = new URLSearchParams();
  params.set('format', 'csv');
  params.set('ids', String(order.id));

  const response = await apiGet('/api/orders/export?' + params.toString());
  // `apiThrow` keeps the server's `error` + `code` (POL2 fix round 1; this
  // threw a fixed 'Export failed', discarding the refusal reason).
  if (!response.ok) await apiThrow(response);

  const blob = await response.blob();
  // 🔀 PO (via poDisplayText, so a legacy `147` is `PO-147`) else the order
  // NUMBER `ORD-004` (2026-08-12), never the production reference.
  const base = poDisplayText(order) || getOrderNumber(order) || `order-${order.id}`;
  const filename = `${base}${CSV_EXT}`;

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export default exportSingleOrderCsv;
